import Link from 'next/link';
import { useRouter } from 'next/router';
import React from 'react';
import { Header } from '@/components/modules/Header';
import { useIsUserCountryForDubaiMid } from '@/hooks';
import { CONSTANTS } from '@/constants';

// Types
interface IProps {}

const PrivacyPolicy: React.FC<IProps> = () => {
	const router = useRouter();
	const isDubaiMid = useIsUserCountryForDubaiMid();

	return (
		<section className='static-page'>
			<Header
				showMenuButton
				showCenterLogo
				action={{
					icon: 'back',
					goBackCallback: () => router.back(),
				}}
			/>
			<div className='container old-styles'>
				<h1>IMPORTANT PRIVACY INFORMATION</h1>
				<p>
					If you use Lift, we will also automatically collect from your device
					language settings, IP address, time zone, type and model of a device,
					device settings, operating system, Internet service provider, mobile
					carrier, hardware ID, and unique identifiers (such as IDFA and AAID).
					We need this data to provide our services, analyze how our customers
					use the app and to measure ads.
				</p>
				<p>
					For improving the website and serving ads, we use third party
					solutions. As a result, we may process data using solutions developed
					by{' '}
					<b>
						Amazon, Amplitude, Appsflyer, Crashlytics, Meta, Google, Firebase,
						Tableau, Zendesk, OpenAI, PayPal, Solidgate, Microsoft, Fal.AI,
						Sentry, Twilio, Trustpilot.
					</b>{' '}
					Therefore, some of the data is stored and processed on the servers of
					such third parties. This enables us to (1) analyze different
					interactions (for example what content users utilize); (2) serve and
					measure ads (and show them only to a particular group of users, for
					example, only to subscribers).
				</p>
				<p>
					Please read our Privacy Policy below to know more about what we do
					with data (
					<Link href='/privacy-policy#contents-3' prefetch={false}>
						Section 3
					</Link>
					), what data privacy rights are available to you (
					<Link href='/privacy-policy#contents-6' prefetch={false}>
						Section 6
					</Link>
					) and who will be the data controller (
					<Link href='/privacy-policy#contents-1' prefetch={false}>
						Section 1
					</Link>
					). If any questions will remain unanswered, please contact us at{' '}
					<a href={`mailto:${CONSTANTS.SUPPORT_MAIL_ADDRESS}`}>
						{CONSTANTS.SUPPORT_MAIL_ADDRESS}
					</a>
					.
				</p>

				<p>
					<strong>Privacy Policy</strong>
				</p>
				<p>
					This Privacy Policy explains what personal data is collected when you
					use the Lift website (the "<b>Lift</b>"), the services and products
					provided through it (together with the Website, the "<b>Service</b>
					"), how such personal data will be processed.
				</p>
				<p>
					BY USING THE SERVICE, YOU PROMISE US THAT (I) YOU HAVE READ,
					UNDERSTAND AND AGREE TO THIS PRIVACY POLICY, AND (II) YOU ARE OVER 16
					YEARS OF AGE (OR HAVE HAD YOUR PARENT OR GUARDIAN READ AND AGREE TO
					THIS PRIVACY POLICY FOR YOU). If you do not agree, or are unable to
					make this promise, you must not use the Service. In such case, you
					must (a) contact us and request deletion of your data; (b) leave the
					Website and not access or use it; and (c) cancel any active
					subscriptions or trials.
				</p>
				<p>
					Any translation from English version is provided for your convenience
					only. In the event of any difference in meaning or interpretation
					between the English language version of this Privacy Policy available
					at liftapp.ai, and any translation, the English language version will
					prevail. The original English text shall be the sole legally binding
					version.
				</p>
				<p>
					<b>"GDPR"</b> means the General Data Protection Regulation (EU)
					2016/679 of the European Parliament and of the Council of 27 April
					2016 on the protection of natural persons with regard to the
					processing of personal data and on the free movement of such data.
				</p>
				<p>
					<b>"EEA"</b> includes all current member states to the European Union
					and the European Free Trade Association. For the purpose of this
					policy EEA shall include the United Kingdom of Great Britain and
					Northern Ireland.
				</p>
				<p>
					<b>"Process"</b>, in respect of personal data, includes to collect,
					store, and disclose to others.
				</p>

				<p id='menu'>
					<strong>TABLE OF CONTENTS</strong>
				</p>
				<p>
					<Link href='/privacy-policy#contents-1' prefetch={false}>
						1. PERSONAL DATA CONTROLLER
					</Link>
				</p>
				<p>
					<Link href='/privacy-policy#contents-2' prefetch={false}>
						2. CATEGORIES OF PERSONAL DATA WE COLLECT
					</Link>
				</p>
				<p>
					<Link href='/privacy-policy#contents-3' prefetch={false}>
						3. FOR WHAT PURPOSES WE PROCESS PERSONAL DATA
					</Link>
				</p>
				<p>
					<Link href='/privacy-policy#contents-4' prefetch={false}>
						4. UNDER WHAT LEGAL BASES WE PROCESS YOUR PERSONAL DATA (Applies
						only to EEA-based users)
					</Link>
				</p>
				<p>
					<Link href='/privacy-policy#contents-5' prefetch={false}>
						5. WITH WHOM WE SHARE YOUR PERSONAL DATA
					</Link>
				</p>
				<p>
					<Link href='/privacy-policy#contents-6' prefetch={false}>
						6. HOW YOU CAN EXERCISE YOUR PRIVACY RIGHTS
					</Link>
				</p>
				<p>
					<Link href='/privacy-policy#contents-7' prefetch={false}>
						7. AGE LIMITATION
					</Link>
				</p>
				<p>
					<Link href='/privacy-policy#contents-8' prefetch={false}>
						8. INTERNATIONAL DATA TRANSFERS
					</Link>
				</p>
				<p>
					<Link href='/privacy-policy#contents-9' prefetch={false}>
						9. CHANGES TO THIS PRIVACY POLICY
					</Link>
				</p>
				<p>
					<Link href='/privacy-policy#contents-10' prefetch={false}>
						10. U.S. STATES PRIVACY NOTICE
					</Link>
				</p>
				<p>
					<Link href='/privacy-policy#contents-11' prefetch={false}>
						11. DATA RETENTION
					</Link>
				</p>
				<p>
					<Link href='/privacy-policy#contents-12' prefetch={false}>
						12. HOW "DO NOT TRACK" REQUESTS ARE HANDLED
					</Link>
				</p>
				<p>
					<Link href='/privacy-policy#contents-13' prefetch={false}>
						13. CONTACT US
					</Link>
				</p>

				<p id='contents-1'>
					<b>
						<Link href='/privacy-policy#menu' prefetch={false}>
							#
						</Link>{' '}
						1. PERSONAL DATA CONTROLLER
					</b>
				</p>
				{isDubaiMid ? (
					<p>
						FT SICH FZCO, a company registered under the laws of the United Arab
						Emirates, having its registered office at IFZA Business Park, DDP,
						Building A2, 21379-001, Dubai, United Arab Emirates, will be the
						controller of your personal data.
					</p>
				) : (
					<p>
						<b>FunplaceApp Limited</b>, a company registered under the laws of
						the Republic of Cyprus, having its registered office at 8
						Ioustinianou Street, Flat 302, 2413 Nicosia, Cyprus, will be the
						controller of your personal data.
					</p>
				)}

				<p id='contents-2'>
					<b>
						<Link href='/privacy-policy#menu' prefetch={false}>
							#
						</Link>{' '}
						2. CATEGORIES OF PERSONAL DATA WE COLLECT
					</b>
				</p>
				<p>
					We collect data you give us voluntarily (for example, your email). We
					also collect data automatically (for example, your IP address).
				</p>
				<p>
					<strong>2.1. Data you give us:</strong>
				</p>
				<p>
					You provide us information about yourself when you register for and/or
					use the Service. For example: email.
				</p>
				<p>
					<strong>2.2. Data provided by third parties:</strong>
				</p>
				<p>
					When you use sign in with Apple to register an account in the Service,
					we get personal data from your Apple ID account. This data may
					include, in particular, your email address. You may choose to share
					your real email address or an anonymous one that uses the private
					email relay service. Apple will show you their detailed privacy
					information on the sign in with Apple screen. Find more about sign
					with Apple <a href='https://support.apple.com/en-ng/HT208650'>here</a>
					.
				</p>
				<p>
					When you decide to log in using Facebook, we get some personal data
					from your Facebook account. Unless you opt out on the Facebook Login
					screen, we will also collect other data, such as email address.
				</p>
				<p>
					For more information, please refer to the Meta{' '}
					<a href='https://developers.facebook.com/docs/facebook-login/permissions'>
						Permissions Reference
					</a>{' '}
					(describes the categories of information, which Meta may share with
					third parties and the set of requirements) and to the Meta{' '}
					<a href='https://www.facebook.com/about/privacy/'>Data policy</a>. In
					addition, Meta lets you control the choices you made when connecting
					your Meta profile to the Website on their{' '}
					<a href='https://www.facebook.com/settings?tab=applications'>
						Apps and Websites page
					</a>
					.
				</p>
				<p>
					When you decide to log in using Google, we receive personal data from
					your Google Account: email address. You can revoke access provided to
					us on{' '}
					<a href='https://myaccount.google.com/permissions'>
						Apps Permissions page
					</a>
					. To know more about how Google processes your data, visit its{' '}
					<a href='https://policies.google.com/privacy'>Privacy Policy</a>.
				</p>
				<p>
					<b>2.3. Data we collect automatically:</b>
				</p>
				<p>2.3.1. Data about how you found us:</p>
				<p>
					We collect data about your referring app or URL (that is, the app or
					place on the Web where you were when you tapped on our ad).
				</p>
				<p>2.3.2. Device and Location data:</p>
				<p>
					We collect data from your mobile device. Examples of such data
					include: language settings, IP address, time zone, type and model of a
					device, device settings, operating system, Internet service provider,
					mobile carrier, hardware ID. We also may record the ads in our Website
					with which you interact (and the Internet links to which those ads
					lead).
				</p>
				<p>2.3.3. Usage data:</p>
				<p>
					We record how you interact with our Service. For example, we log what
					pages you have viewed, the features and content you interact with, how
					often you use the Website, how long you are on the Website, your
					purchases.
				</p>
				<p>2.3.4. Transaction data:</p>
				<p>
					When you make payments through the Service, you need to provide
					financial account data, such as your credit card number, to our
					third-party service providers. We do not collect or store full credit
					card number data, though we may receive credit card-related data, data
					about the transaction, including: date, time and amount of the
					transaction, the type of payment method used.
				</p>
				<p>2.3.5. Cookies:</p>
				<p>
					A cookie is a small text file that is stored on a user's computer for
					record-keeping purposes. Cookies can be either session cookies or
					persistent cookies. A session cookie expires when you close your
					browser and is used to make it easier for you to navigate our Service.
					A persistent cookie remains on your hard drive for an extended period
					of time. We also use tracking pixels that set cookies to assist with
					delivering online advertising.
				</p>
				<p>
					Cookies are used, in particular, to automatically recognize you the
					next time you visit our website. As a result, the information, which
					you have earlier entered in certain fields on the website may
					automatically appear the next time when you use our Service. Cookie
					data will be stored on your device and most of the times only for a
					limited time period.
				</p>

				<p id='contents-3'>
					<b>
						<Link href='/privacy-policy#menu' prefetch={false}>
							#
						</Link>{' '}
						3. FOR WHAT PURPOSES WE PROCESS YOUR PERSONAL DATA
					</b>
				</p>
				<p>We process your personal data:</p>
				<p>
					<strong>3.1. To provide our Service</strong>
				</p>
				<p>
					This includes enabling you to use the Service in a seamless manner and
					preventing or addressing Service errors or technical issues. As a
					result of such processing, we will use your email, for example, for
					sending you emails regarding our product.
				</p>
				<p>
					We use <b>OpenAI</b> for AI-based image generation and editing
					services. When you request image creation or modification through our
					website, we will process your requests via OpenAI's artificial
					intelligence platform, which enables us to generate, edit, and enhance
					digital imagery according to your specifications.
				</p>
				<p>
					We use <b>Gemini</b> for AI-based photo filtering and editing
					services. When you request photo modifications through our Website, we
					will process your requests via Google's Gemini artificial intelligence
					platform, which enables us to apply filters, edit, and enhance your
					images according to your specifications.
				</p>
				<p>
					We use <b>Fal.ai</b> to process and generate visual content from your
					photos and videos. When you upload photos or videos for processing,
					Fal.ai applies AI models to transform, enhance, or generate new
					content based on your input.
				</p>
				<p>
					To host personal data and enable our Service to operate and be
					distributed we use <b>Amazon Web Services</b>, which is a hosting and
					backend service provided by Amazon.
				</p>
				<p>
					We use <b>Google Cloud Platform</b> services to host, store, and
					process data related to our Website and services. To learn more about
					Google Cloud's data handling and security practices, please visit
					Google Cloud's Privacy Notice.
				</p>
				<p>
					To monitor infrastructure and the App's performance, we use{' '}
					<b>Crashlytics</b>, which is a monitoring service provided by Google.{' '}
					<a href='https://firebase.google.com/support/privacy/'>
						Privacy and Security in Firebase
					</a>
					.
				</p>
				<p>
					We use <b>Firebase Crash Reporting</b> to monitor and improve
					application stability. When our application encounters errors or
					crashes on your device, we will collect crash data and diagnostic
					information via Firebase Crash Reporting service, which enables us to
					identify, analyze and resolve technical issues to enhance app
					performance.
				</p>
				<p>
					We use <b>Google Tag Manager</b> to manage website tracking and
					analytics tools. When you visit our website or use our services, we
					will deploy various tracking tags and scripts via Google Tag Manager,
					which enables us to monitor website performance, user interactions and
					optimize our services.
				</p>
				<p>
					We use <b>Microsoft Clarity</b> to analyze user interactions and
					improve application usability. Microsoft Clarity captures how users
					navigate and interact with our Services through session recordings,
					heatmaps, and behavioral analytics. When you use our Services, Clarity
					may collect data such as mouse movements, clicks, scrolls, and page
					rendering information, which enables us to identify usability issues,
					optimize user experience, and enhance overall application performance.
				</p>
				<p>
					We use <b>Sentry</b> to monitor and resolve technical errors in our
					Services. When errors or exceptions occur during your use of the
					Services, Sentry captures diagnostic information including error
					messages, stack traces, device information, and the context in which
					issues occurred. This allows us to quickly identify, diagnose, and fix
					technical problems to maintain application stability and improve your
					experience.
				</p>
				<p>
					<strong>3.2. To provide you with customer support</strong>
				</p>
				<p>
					We process your personal data to respond to your requests for
					technical support, Service information or to any other communication
					you initiate. For this purpose, we may send you, for example,
					notifications or emails about, the performance of our Service,
					security, payment transactions, notices regarding our{' '}
					<Link href='/terms-of-use' prefetch={false}>
						Terms and Conditions
					</Link>{' '}
					of Use or this Privacy Policy.
				</p>
				<p>
					<strong>
						3.3. To communicate with you regarding your use of our Service
					</strong>
				</p>
				<p>
					We communicate with you, for example, by emails. These may include for
					example, emails with information about the Service. To opt out of
					receiving emails, you need to follow the instructions in the footer of
					the emails.
				</p>
				<p>
					The services that we use for these purposes may collect data
					concerning the date and time when the message was viewed by our
					Service's users, as well as when they interacted with it, such as by
					tapping on links included in the message.
				</p>
				<p>
					We use <b>Zendesk</b> ticketing system to handle customer inquiries.
					When you send us inquiries via contact form or via email, we will
					store the details provided by you via Zendesk ticketing system, which
					enables us to track, prioritize and quickly solve your requests.{' '}
					<a href='https://www.zendesk.com/company/customers-partners/privacy-policy/'>
						Privacy Policy
					</a>
					.
				</p>
				<p>
					We use <b>Twilio</b> to deliver email communications from our
					Services. When you send or receive emails through our Services, Twilio
					manages the technical aspects of email delivery, including
					transactional emails, notifications, and other messages. This ensures
					reliable email delivery and helps us monitor email performance.
				</p>
				<p>
					<strong>3.4. To research and analyze your use of the Service</strong>
				</p>
				<p>
					This helps us to better understand our business, analyze our
					operations, maintain, improve, innovate, plan, design, and develop the
					Service and our new products. We also use such data for statistical
					analysis purposes, to test and improve our offers. This enables us to
					better understand what categories of users use our Services. As a
					consequence, we often decide how to improve the Service based on the
					results obtained from this processing.
				</p>
				<p>
					To perform research and analysis about how users interact with our
					Service we use <b>Appsflyer</b>. Appsflyer enables us to understand,
					in particular, how users find us (for example, who was the advertiser
					that delivered an ad to users, which led you to our Website).
					Appsflyer also provides us with different analytics tools that enable
					us to research and analyze your use of the Service.{' '}
					<a href='https://www.appsflyer.com/privacy-policy/'>Privacy Policy</a>
					. Appsflyer allows you to{' '}
					<a href='https://www.appsflyer.com/optout'>Opt Out</a> of having data
					from my device sent to Appsflyer's servers for apps usage collection.
				</p>
				<p>
					<b>Amplitude</b> is an analytics service provided by Amplitude Inc. We
					use this tool to understand how customers use our Service. Amplitude
					collects various technical information, in particular, time zone, type
					of device (phone, tablet or laptop), unique identifiers (including
					advertising identifiers). Amplitude also allows us to track various
					interactions that occur in our Service. As a result, Amplitude helps
					us to decide what features we should focus on. Amplitude provides more
					information on how they process data in its{' '}
					<a href='https://amplitude.com/privacy'>Privacy Policy</a>.
				</p>
				<p>
					We also use <b>Firebase Analytics</b>, which is an analytics service
					provided by Google. In order to understand Google's use of data,
					consult Google's{' '}
					<a href='https://policies.google.com/technologies/partner-sites?hl=ru'>
						partner policy
					</a>
					. Firebase{' '}
					<a href='https://firebase.google.com/support/privacy/'>
						Privacy information
					</a>
					.{' '}
					<a href='https://policies.google.com/privacy'>
						Google's Privacy Policy
					</a>
					.
				</p>
				<p>
					To perform our Website analysis, we also use <b>Tableau</b>, a company
					providing interactive data visualization products focused on business
					intelligence.{' '}
					<a href='https://www.tableau.com/privacy'>
						Tableau Software Privacy Policy
					</a>
					.
				</p>
				<p>
					To analyse how visitors use our Service and to measure effectiveness
					of some ads we use <b>Google Analytics</b>, a web analysis program of
					Google. In order to provide us with analytics, Google Analytics places
					cookies on your device. On Google Analytics we get, in particular,
					aggregated information on the data you enter on our Website and users'
					interactions within the Website. Google allows you to influence the
					collection and processing of information generated by the Google, in
					particular, by installing a browser plug-in, available{' '}
					<a href='https://tools.google.com/dlpage/gaoptout/'>here</a>. You can
					read more about how Google uses information{' '}
					<a href='https://policies.google.com/technologies/partner-sites?hl=en'>
						here
					</a>
					.
				</p>
				<p>
					<b>3.5. To send you marketing communications</b>
				</p>
				<p>
					We process your personal data for our marketing campaigns. As a
					result, you will receive information about our products, such as, for
					example, special offers or new features and products available on the
					Website. We may show you advertisements on our Website, and send you
					emails for marketing purposes. If you do not want to receive marketing
					emails from us, you can unsubscribe following instructions in the
					footer of the marketing emails.
				</p>
				<p>
					<b>3.6. To personalize our ads</b>
				</p>
				<p>
					We and our partners use your personal data to tailor ads and possibly
					even show them to you at the relevant time. For example, if you have
					visited our Website, you might see ads of our products in your Meta's
					feed.
				</p>
				<p>
					<b>How to opt out or influence personalized advertising</b>
				</p>
				<p>
					<b>iOS:</b> On your iPhone or iPad, go to Settings {'>'} Privacy {'>'}{' '}
					Apple Advertising and deselect Personalized Ads.
				</p>
				<p>
					<b>Android:</b> To opt-out of ads on an Android device, go to Settings{' '}
					{'>'} Privacy {'>'} Ads and enable Opt out of Ads personalization. In
					addition, you can reset your advertising identifier in the same
					section (this also may help you to see less of personalized ads). To
					learn even more about how to affect advertising choices on various
					devices, please look at the information available{' '}
					<a href='http://www.networkadvertising.org/mobile-choice'>here</a>.
				</p>
				<p>
					<b>macOS:</b> On your MacBook, you can disable personalized ads: go to
					System Preferences {'>'} Security & Privacy {'>'} Privacy, select
					Apple Advertising, and deselect Personalized Ads.
				</p>
				<p>
					<b>Windows:</b> On your laptop running Windows 10, you shall select
					Start {'>'} Settings {'>'} Privacy and then turn off the setting for
					Let apps use advertising ID to make ads more interesting to you based
					on your app activity. If you have other Windows version, please follow
					the steps{' '}
					<a href='https://account.microsoft.com/privacy/ad-settings/signedout?lang=en-GB'>
						here
					</a>
				</p>
				<p>
					To learn even more about how to affect advertising choices on various
					devices, please look at the information available{' '}
					<a href='http://www.networkadvertising.org/mobile-choice'>here</a>.
				</p>
				<p>
					In addition, you may get useful information and opt out of some
					interest-based advertising, by visiting the following links:
				</p>
				<ul>
					<li>
						Network Advertising Initiative -&nbsp;
						<a href='http://optout.networkadvertising.org/'>
							http://optout.networkadvertising.org/
						</a>
					</li>
					<li>
						Digital Advertising Alliance -&nbsp;
						<a href='http://optout.aboutads.info/'>
							http://optout.aboutads.info/
						</a>
					</li>
					<li>
						Digital Advertising Alliance (Canada) -&nbsp;
						<a href='http://youradchoices.ca/choices'>
							http://youradchoices.ca/choices
						</a>
					</li>
					<li>
						Digital Advertising Alliance (EU) -&nbsp;
						<a href='http://www.youronlinechoices.com/'>
							http://www.youronlinechoices.com/
						</a>
					</li>
					<li>
						DAA AppChoices page -&nbsp;
						<a href='http://www.aboutads.info/appchoices'>
							http://www.aboutads.info/appchoices
						</a>
					</li>
				</ul>
				<p>
					<b>Browsers:</b> It is also may be possible to stop your browser from
					accepting cookies altogether by changing your browser's cookie
					settings. You can usually find these settings in the "options" or
					"preferences" menu of your browser. The following links may be
					helpful, or you can use the "Help" option in your browser.
				</p>
				<ul>
					<li>
						<a href='http://windows.microsoft.com/en-GB/internet-explorer/delete-manage-cookies#ie=ie-10'>
							Cookie settings in Internet Explorer
						</a>
					</li>
					<li>
						<a href='http://support.mozilla.com/en-US/kb/Cookies'>
							Cookie settings in Firefox
						</a>
					</li>
					<li>
						<a href='https://support.google.com/chrome/answer/95647?hl=en&ref_topic=14666'>
							Cookie settings in Chrome
						</a>
					</li>
					<li>
						<a href='https://support.apple.com/kb/PH17191?locale=en_US'>
							Cookie settings in Safari web
						</a>
						&nbsp;and&nbsp;<a href='http://support.apple.com/kb/HT1677'>iOS</a>
					</li>
				</ul>
				<p>
					Google allows its users to{' '}
					<a href='https://adssettings.google.com/authenticated?hl=ru'>
						opt out of Google's personalized ads
					</a>{' '}
					and to{' '}
					<a href='https://tools.google.com/dlpage/gaoptout/'>
						prevent their data from being used by Google Analytics
					</a>
					.
				</p>
				<p>
					We value your right to influence the ads that you see, thus we are
					letting you know what service providers we use for this purpose and
					how some of them allow you to control your ad preferences.
				</p>
				<p>
					We use <b>Meta Ads Manager</b>, an advertising tool that allows us to
					select audiences for our ads on Meta and other Meta products (such as
					Instagram). Meta Ads Manager may collect and use certain information
					about your visits to our Website, including your device information,
					browsing history, and location data. We may provide Meta with lists of
					users to whom we want to show our ads. As a result, you may see more
					of our ads while using Meta or other Meta products (such as
					Instagram). Meta allows its users to control the types of ads they see
					on Meta. To learn how to manage the ads you see, please visit Meta's{' '}
					<a href='https://www.facebook.com/ads/preferences/?entry_product=ad_settings_screen'>
						ad preferences
					</a>{' '}
					or adjust your ads settings directly on Meta.
				</p>
				<p>
					We use the <b>Meta Audience Network SDK</b> in our mobile application
					to display advertisements from Meta's advertising partners. This SDK
					may collect and use certain information about your device and app
					usage, including device identifiers, IP address, location data, and
					your interactions within the app. Meta uses this information to
					deliver personalized ads and measure ad performance. The ads you see
					through Meta Audience Network may be based on your activity across
					apps and websites. You can control how Meta uses your information for
					advertising purposes by visiting Meta's ad settings or adjusting your
					device's advertising preferences.
				</p>
				<p>
					<b>Google Ads</b> is an ad delivery service provided by Google that
					can deliver ads to users. In particular, Google allows us to tailor
					the ads in a way that they will appear, for example, only to users
					that have conducted certain actions with our Service (for example,
					show our ads to users who have made a purchase). Google allows its
					users to{' '}
					<a href='https://adssettings.google.com/authenticated?hl=ru'>
						opt out of Google's personalized ads
					</a>{' '}
					and to{' '}
					<a href='https://tools.google.com/dlpage/gaoptout/'>
						prevent their data from being used by Google Analytics
					</a>
					.
				</p>
				<p>
					<b>Microsoft Ads</b> is an advertising platform that enables
					businesses to display advertisements across the Bing search network
					and partner properties. We use this service to test and run
					advertising campaigns, measure ad performance, track user acquisition
					metrics, and evaluate the effectiveness of our marketing efforts in
					reaching potential users.
				</p>
				<p>
					<b>3.7. To process your payments</b>
				</p>
				<p>
					We provide paid products and/or services within the Service. For this
					purpose, we use third-party services for payment processing (for
					example, payment processors). As a result of this processing, you will
					be able to make a payment and we will be notified that the payment has
					been made.
				</p>
				<p>
					We will not store or collect your payment card details ourselves. This
					information will be provided directly to our third-party payment
					processors.
				</p>
				<p>
					To enable the purchase and to process your payments we use{' '}
					<b>Solidgate</b> and <b>PayPal</b>, which are payment processing
					providers.
				</p>
				<p>
					<b>
						3.8. To enforce our Terms and Conditions of Use and to prevent and
						combat fraud
					</b>
				</p>
				<p>
					We use personal data to enforce our agreements and contractual
					commitments, to detect, prevent, and combat fraud. As a result of such
					processing, we may share your information with others, including law
					enforcement agencies (in particular, if a dispute arises in connection
					with our{' '}
					<Link href='terms-of-use' prefetch={false}>
						Terms and Conditions of Use
					</Link>
					).
				</p>
				<p>
					<b>3.9. To comply with legal obligations</b>
				</p>
				<p>
					We may process, use, or share your data when the law requires it, in
					particular, if a law enforcement agency requests your data by
					available legal means.
				</p>

				<p>
					<b>3.10. To collect your feedback about the Service</b>
				</p>

				<p>
					We may ask you to rate the Service and share your feedback. If you
					share feedback directly with us, we use it to improve the Service. We
					use Trustpilot, a review platform provided by Trustpilot A/S
					(Denmark), to invite our users to leave reviews. For this purpose, we
					may share your name, email address and an internal reference number
					with Trustpilot, which will send you an email inviting you to review
					the Service on Trustpilot on our behalf. When sending invitations,
					Trustpilot acts as our service provider (data processor). If you
					decide to write a review, Trustpilot processes your review and account
					data as an independent controller under its Privacy Policy. You can
					object to receiving review invitations at any time by contacting us at
					support@liftapp.ai or by using the unsubscribe link in the invitation
					email.
				</p>

				<p id='contents-4'>
					<b>
						<Link href='/privacy-policy#menu' prefetch={false}>
							#
						</Link>{' '}
						4. UNDER WHAT LEGAL BASES WE PROCESS YOUR PERSONAL DATA{' '}
					</b>
				</p>
				<p>
					In this section, we are letting you know what legal basis we use for
					each particular purpose of processing. For more information on a
					particular purpose, please refer to{' '}
					<Link href='/privacy-policy#contents-3' prefetch={false}>
						Section 3
					</Link>
					. This section applies only to EEA-based users.
				</p>
				<p>We process your personal data under the following legal bases: </p>
				<p>
					<b>4.1. Your consent</b>
				</p>
				<p>Under this legal basis we:</p>
				<ul>
					<li>
						Send you marketing communications. You have the right to withdraw
						your consent any time by using the unsubscribe link in the footer of
						our emails.
					</li>
					<li>
						Store or access information on your devices via cookies and other
						tracking technologies.
					</li>
				</ul>
				<p>
					<b>4.2. To perform our contract with you;</b>
				</p>
				<p>Under this legal basis we:</p>
				<ul>
					<li>
						Provide our Service (in accordance with our Terms and Conditions of
						Use)
					</li>
					<li>Customize your experience</li>
					<li>Provide you with customer support</li>
					<li>Communicate with you regarding your use of our Service</li>
					<li>Process your payments</li>
				</ul>
				<p>
					<b>
						4.3. for our (or others') legitimate interests, unless those
						interests are overridden by your interests or fundamental rights and
						freedoms that require protection of personal data;
					</b>
				</p>
				<p>We rely on legitimate interests:</p>
				<ul>
					<li>
						<p>to research and analyze your use of the Service</p>
						<p>
							Our legitimate interest for this purpose is our interest in
							improving our Service so that we understand users' preferences and
							are able to provide you with a better experience (for example, to
							make the use of the Website easier and more enjoyable, or to
							introduce and test new features).
						</p>
					</li>
					<li>
						<p>to send you marketing communications</p>
						<p>
							The legitimate interest we rely on for this processing is our
							interest to promote our Service, including new products and
							special offers, in a measured and appropriate way.
						</p>
					</li>
					<li>
						<p>to personalize our ads</p>
						<p>
							The legitimate interest we rely on for this processing is our
							interest to promote our Service in a reasonably targeted way.
						</p>
					</li>
					<li>
						<p>
							to enforce our Terms and Conditions of Use and to prevent and
							combat fraud
						</p>
						<p>
							Our legitimate interests for this purpose are enforcing our legal
							rights, preventing and addressing fraud and unauthorised use of
							the Service, non-compliance with our Terms and Conditions of Use.
						</p>
					</li>
					<li>
						<p>to collect your feedback about the Service</p>
						<p>
							Our legitimate interest for this purpose is our interest in
							obtaining honest feedback about the Service, improving it, and
							helping other users make informed decisions.
						</p>
					</li>
				</ul>
				<p>
					<b>4.4. to comply with legal obligations.</b>
				</p>

				<p id='contents-5'>
					<b>
						<Link href='/privacy-policy#menu' prefetch={false}>
							#
						</Link>{' '}
						5. WITH WHOM WE SHARE YOUR PERSONAL DATA
					</b>
				</p>
				<p>
					We share information with third parties that help us operate, provide,
					improve, integrate, customize, support, and market our Service. We may
					share some sets of personal data, in particular, for purposes and with
					parties indicated in{' '}
					<Link href='/privacy-policy#contents-3' prefetch={false}>
						Section 3
					</Link>{' '}
					of this Privacy Policy. The types of third parties we share
					information with include, in particular:{' '}
				</p>
				<p>
					<b>5.1. Service providers</b>
				</p>
				<p>
					We share personal data with third parties that we hire to provide
					services or perform business functions on our behalf, based on our
					instructions. We may share your personal information with the
					following types of service providers:
				</p>
				<ul>
					<li>cloud storage providers (Amazon, Google)</li>
					<li>AI technology providers (OpenAI, Gemini, Fal.AI)</li>
					<li>
						data analytics providers (Amplitude, Microsoft, Sentry, Appsflyer,
						Crashlytics, Firebase, Google, Tableau)
					</li>
					<li>measurement partners (Meta, Google)</li>
					<li>communication service providers (Zendesk, Twilio)</li>
					<li>
						marketing partners (in particular, social media networks, marketing
						agencies, email delivery services; Meta, Google, Microsoft)
					</li>
					<li>Payment processing services (Solidgate, PayPal)</li>
					<li>review platforms (Trustpilot)</li>
				</ul>
				<p>
					<b>5.2. Law enforcement agencies and other public authorities</b>
				</p>
				<p>
					We may use and disclose personal data to enforce our{' '}
					<Link href='terms-of-use' prefetch={false}>
						Terms and Conditions of Use
					</Link>
					, to protect our rights, privacy, safety, or property, and/or that of
					our affiliates, you or others, and to respond to requests from courts,
					law enforcement agencies, regulatory agencies, and other public and
					government authorities, or in other cases provided for by law.
				</p>
				<p>
					<b>5.3. Third parties as part of a merger or acquisition</b>
				</p>
				<p>
					As we develop our business, we may buy or sell assets or business
					offerings. Customers' information is generally one of the transferred
					business assets in these types of transactions. We may also share such
					information with any affiliated entity (e.g. parent company or
					subsidiary) and may transfer such information in the course of a
					corporate transaction, such as the sale of our business, a
					divestiture, merger, consolidation, or asset sale, or in the unlikely
					event of bankruptcy.
				</p>

				<p id='contents-6'>
					<b>
						<Link href='/privacy-policy#menu' prefetch={false}>
							#
						</Link>{' '}
						6. HOW YOU CAN EXERCISE YOUR PRIVACY RIGHTS
					</b>
				</p>
				<p>
					To be in control of your personal data, you have the following rights:
				</p>
				<p>
					<b>
						Accessing / reviewing / updating / correcting your personal data
					</b>
					. You may review, edit, or change the personal data that you had
					previously provided on the Service.
				</p>
				<p>
					<b>Deleting your personal data.</b> You can request erasure of your
					personal data as permitted by law.
				</p>
				<p>
					When you request deletion of your personal data, we will use
					reasonable efforts to honor your request. In some cases, we may be
					legally required to keep some of the data for a certain time; in such
					event, we will fulfill your request after we have complied with our
					obligations.
				</p>
				<p>
					<b>Objecting to or restricting the use of your personal data</b>. You
					can ask us to stop using all or some of your personal data or limit
					our use thereof.
				</p>
				<p>
					<b>Additional information for EEA-based users:</b>
				</p>
				<p>
					If you are based in the EEA, you have the following rights in addition
					to the above:
				</p>
				<p>
					<b>The right to lodge a complaint with supervisory authority</b>. We
					would love you to contact us directly, so we could address your
					concerns. Nevertheless, you have the right to lodge a complaint with a
					competent data protection supervisory authority, in particular in the
					EU Member State where you reside, work or where the alleged
					infringement has taken place.
				</p>
				<p>
					<b>The right to data portability</b>. If you wish to receive your
					personal data in a machine-readable format, you can send respective
					request to us as described below.
				</p>
				<p>
					To exercise any of your privacy rights, please send a request to{' '}
					<a href={`mailto:${CONSTANTS.SUPPORT_MAIL_ADDRESS}`}>
						{CONSTANTS.SUPPORT_MAIL_ADDRESS}
					</a>
					.
				</p>

				<p id='contents-7'>
					<b>
						<Link href='/privacy-policy#menu' prefetch={false}>
							#
						</Link>{' '}
						7. AGE LIMITATION
					</b>
				</p>
				<p>
					We do not knowingly process personal data from persons under 16 years
					of age. If you learn that anyone younger than 16 has provided us with
					personal data, please contact us.
				</p>

				<p id='contents-8'>
					<b>
						<Link href='/privacy-policy#menu' prefetch={false}>
							#
						</Link>{' '}
						8. INTERNATIONAL DATA TRANSFERS
					</b>
				</p>
				<p>
					We may transfer personal data to countries other than the country in
					which the data was originally collected in order to provide the
					Service set forth in the{' '}
					<Link href='/terms-of-use' prefetch={false}>
						Terms and Conditions of Use
					</Link>{' '}
					and for purposes indicated in this Privacy Policy. If these countries
					do not have the same data protection laws as the country in which you
					initially provided the information, we deploy special safeguards.
				</p>
				<p>
					In particular, if we transfer personal data originating from the EEA
					to countries with not adequate level of data protection, we use one of
					the following legal bases: (i) Standard Contractual Clauses approved
					by the European Commission (details available{' '}
					<a href='https://commission.europa.eu/law/law-topic/data-protection/international-dimension-data-protection/standard-contractual-clauses-scc_en'>
						here
					</a>
					), or (ii) the European Commission adequacy decisions about certain
					countries (details available{' '}
					<a href='https://commission.europa.eu/law/law-topic/data-protection/international-dimension-data-protection_en'>
						here
					</a>
					).
				</p>

				<p id='contents-9'>
					<b>
						<Link href='/privacy-policy#menu' prefetch={false}>
							#
						</Link>{' '}
						9. CHANGES TO THIS PRIVACY POLICY
					</b>
				</p>
				<p>
					We may modify this Privacy Policy from time to time. If we decide to
					make material changes to this Privacy Policy, you will be notified by
					available means such as email and will have an opportunity to review
					the revised Privacy Policy. By continuing to access or use the Service
					after those changes become effective, you agree to be bound by the
					revised Privacy Policy.
				</p>

				<p id='contents-10'>
					<b>
						<Link href='/privacy-policy#menu' prefetch={false}>
							#
						</Link>{' '}
						10. U.S. STATES PRIVACY NOTICE
					</b>
				</p>
				<p>
					<b>Applicability</b>
				</p>
				<p>
					If you reside in a state that has enacted consumer privacy laws, this
					section applies to you. This U.S. States Privacy Notice ("Notice")
					supplements our Privacy Policy and provides disclosures required by
					laws in states such as{' '}
					<b>
						California, Colorado, Connecticut, Delaware, Iowa, Montana,
						Nebraska, New Hampshire, New Jersey, Oregon, Texas, Utah, and
						Virginia
					</b>
					.
				</p>
				<p>
					This Notice is designed to supplement our Privacy Policy to ensure
					compliance with state-specific laws and applies to the collection,
					use, and sharing of your Personal Information through our websites,
					mobile applications, and other online or offline services
					(collectively, the "Services").
				</p>
				<p>
					For California residents, this also serves as our California Notice at
					Collection.
				</p>
				<p>
					The definition of "Personal Information" may vary by state law.
					Generally, it refers to "information that identifies, relates to,
					describes, is capable of being associated with, or could reasonably be
					linked, directly or indirectly, with a particular consumer or
					household."
				</p>
				<p>
					<b>Categories of Personal Information We Collect</b>
				</p>
				<p>
					The table below outlines the categories of Personal Information we
					collect, along with examples. For more details, refer to our Privacy
					Policy.
				</p>
				<p>
					For additional details on the purposes of processing and third parties
					with whom data is shared for each specific purpose, please refer to{' '}
					<Link href='/privacy-policy#contents-3' prefetch={false}>
						Section 3
					</Link>{' '}
					of this Privacy Policy.
				</p>
				<p>
					<b>What Personal Information We Share</b>
				</p>
				<p>
					Certain state privacy laws, such as those in California require us to
					disclose the categories of Personal Information that we have shared
					with third parties for business purposes over the past 12 months.
					During this period, we have disclosed all categories of Personal
					Information outlined in the "Categories of Personal Information We
					Collect" section for business purposes. For example, we may share IP
					addresses and device identifiers with service providers that assist us
					with crash monitoring and reporting.
				</p>
				<p>
					Some U.S. states <b>grant residents the right to opt out</b> of
					sharing their <b>Personal Information</b> with third parties in
					exchange for valuable consideration (which may be classified as a{' '}
					<b>"sale"</b> or <b>"share"</b> under state privacy laws, even if no
					monetary transaction occurs). If you reside in one of these states and
					would like to <b>limit the disclosure</b> of your Personal Information
					to third parties for <b>advertising or marketing purposes</b>, please
					refer to the Section 10.4. below.
				</p>
				<p>
					<b>Your Rights</b>
				</p>
				<p>
					Certain U.S. state privacy laws grant residents specific rights
					regarding their personal information. If you reside in a state with
					such laws, you may have the following rights:
				</p>
				<p>
					<b>Right to Data Portability/Access</b>
				</p>
				<p>
					You may have the right to request access to the specific pieces of
					personal information we have collected about you in the 12 months
					preceding your request. Where applicable, we may provide this data in
					an electronic, portable, and readily usable format.
				</p>
				<p>
					<b>Right to Know</b>
				</p>
				<p>
					You may be entitled to receive information regarding the categories of
					Personal Information we collected, the sources from which we collected
					Personal Information, the purposes for which we collected and shared
					Personal Information, the categories of Personal Information that we
					sold and the categories of third parties to whom the Personal
					Information was sold, and the categories of Personal Information that
					we disclosed for a business purpose in the 12 months preceding your
					request.
				</p>
				<p>
					<b>Right to Deletion</b>
				</p>
				<p>
					You may have the right to request that we delete the personal
					information we have collected from you. We will use commercially
					reasonable efforts to fulfill your request, subject to applicable
					laws. However, we may be required to retain certain information for
					legitimate business purposes or as required by law.
				</p>
				<p>
					<b>Right to Opt-Out of Sales, Sharing, and Targeted Advertising</b>
				</p>
				<p>
					You may have the right to opt out of certain uses of your personal
					information, including:
				</p>
				<ul>
					<li>
						The "sale" or "sharing" of your personal information as defined
						under state privacy laws.
					</li>
					<li>
						The use of your personal information for targeted advertising.
					</li>
				</ul>
				<p>To opt out, you can submit a request via our support team.</p>
				<p>
					<b>Right to Limit the Use of Sensitive Personal Information</b>
				</p>
				<p>
					Certain states provide the right to restrict how businesses use
					sensitive personal information. If applicable, you may contact us at{' '}
					<a href={`mailto:${CONSTANTS.SUPPORT_MAIL_ADDRESS}`}>
						{CONSTANTS.SUPPORT_MAIL_ADDRESS}
					</a>{' '}
					with the subject line "Limit the Use of Sensitive Personal
					Information". We will process such requests in accordance with
					applicable laws.
				</p>
				<p>
					To exercise any of the available privacy rights, please send a request
					to{' '}
					<a href={`mailto:${CONSTANTS.SUPPORT_MAIL_ADDRESS}`}>
						{CONSTANTS.SUPPORT_MAIL_ADDRESS}
					</a>
					.
				</p>
				<p>
					<b>Verification</b>
				</p>
				<p>
					To ensure that we properly handle the requests you make regarding your
					rights, we are required to verify those requests. Depending on the
					type of request, this may include your email, date of subscription
					purchase, date of last activity, date of account creation or some
					other Service use data that will reasonably identify you as an owner
					of the account. We may also ask you for additional proof of identity,
					if necessary.
				</p>
				<p>
					<b>Right to Appeal</b>
				</p>
				<p>
					If we deny your request, you may have the right to appeal our
					decision. To do so, please contact us and explain your concerns. If
					you are dissatisfied with the result of the appeal, you may escalate
					the matter by contacting the Attorney General's office in your state
					of residence.
				</p>
				<p>
					<b>Authorized Agents</b>
				</p>
				<p>
					If you are an authorized agent acting on behalf of a consumer, you may
					submit an opt-out, access, deletion, or correction request by
					contacting us. If you are making a request on behalf of a California
					resident, you may be required to submit proof of authorization, such
					as:
				</p>
				<ul>
					<li>A valid power of attorney</li>
					<li>
						Written permission signed by the consumer, along with identity
						verification from the consumer.
					</li>
				</ul>
				<p>
					<b>Non-Discrimination</b>
				</p>
				<p>
					You have the right to exercise your privacy rights without fear of
					discrimination or retaliation. However, we may provide different
					levels of service or pricing based on the value of your personal
					information, as permitted by applicable law.
				</p>
				<p>
					<b>Data Retention</b>
				</p>
				<p>
					We retain the categories of personal information listed above as
					reasonably necessary to fulfill the purposes outlined in this notice,
					unless a longer retention period is required or permitted by law. In
					many situations, we must retain all, or a portion, of your personal
					information to comply with legal obligations, resolve disputes,
					enforce our agreements, protect against fraudulent, deceptive, or
					illegal activity, or for another one of our business purposes.
				</p>
				<p>
					<b>Sales, Sharing, and Targeted Advertising</b>
				</p>
				<p>
					Privacy laws in some U.S. states <b>broadly define the term "sale"</b>{' '}
					to include <b>sharing of information</b> via cookies, pixels, and
					similar tracking technologies for certain targeted advertising
					activities.{' '}
					<b>We do not sell Personal Information for monetary compensation.</b>{' '}
					However, when you visit our websites, we and our advertising partners
					may collect <b>device data and behavioral insights</b> through
					tracking technologies, which could be interpreted as a{' '}
					<b>"sale" or "sharing"</b> under state laws, even if no money is
					exchanged.
				</p>
				<p>
					<b>Opt-Out Rights</b>
				</p>
				<p>
					You may opt out of sales, sharing, or targeted advertising by
					contacting us at{' '}
					<a href={`mailto:${CONSTANTS.SUPPORT_MAIL_ADDRESS}`}>
						{CONSTANTS.SUPPORT_MAIL_ADDRESS}
					</a>
					.
				</p>

				<p id='contents-11'>
					<b>
						<Link href='/privacy-policy#menu' prefetch={false}>
							#
						</Link>{' '}
						11. DATA RETENTION
					</b>
				</p>
				<p>
					We will store your personal data for as long as it is reasonably
					necessary for achieving the purposes set forth in this Privacy Policy
					(including providing the Service to you). We will also retain and use
					your personal data as necessary to comply with our legal obligations,
					resolve disputes, and enforce our agreements.
				</p>

				<p id='contents-12'>
					<b>
						<Link href='/privacy-policy#menu' prefetch={false}>
							#
						</Link>{' '}
						12. HOW "DO NOT TRACK" REQUESTS ARE HANDLED
					</b>
				</p>
				<p>
					Except as otherwise stipulated in this Privacy Policy, this App does
					not support "Do Not Track" requests. To determine whether any of the
					third-party services it uses honor the "Do Not Track" requests, please
					read their privacy policies.
				</p>

				<p id='contents-13'>
					<b>
						<Link href='/privacy-policy#menu' prefetch={false}>
							#
						</Link>{' '}
						13. CONTACT US
					</b>
				</p>
				<p>
					You may contact us at any time for details regarding this Privacy
					Policy and its previous versions. For any questions concerning your
					account or your personal data please contact us at{' '}
					<a href={`mailto:${CONSTANTS.SUPPORT_MAIL_ADDRESS}`}>
						{CONSTANTS.SUPPORT_MAIL_ADDRESS}
					</a>
					.
				</p>
				<p>Effective as of: May 7, 2026</p>
			</div>
		</section>
	);
};

export default PrivacyPolicy;
